import Entity from "components/Entity";
import { getAutoMenuActionsCode } from "helpers/getAutoMenuActionsCode";

// Local modular imports
import type { InvoiceProps } from "./Invoice.types";
import { validationSchemaCreate } from "./Invoice.validation";
import { fetchConfig } from "./Invoice.utils";
import {
  supplierFeedbackConditionalFields,
  supplierFeedbackViewConditionalFields,
} from "./Invoice.config";
import {
  rightsRendererCreate,
  readOnlyRightsRendererCreate,
  readOnlyRightsRendererView,
  readOnlyRightsRendererUpdate,
} from "./Invoice.renderers";
import { useInvoice } from "./Invoice.hooks";

/**
 * Invoice detail/form component
 * Handles create, view, and update actions for invoices with proper role-based permissions
 */
function Invoice(props: InvoiceProps): JSX.Element {
  const {
    actionType,
    selectedData,
    idFromRowOption,
    submitButtonText,
    hideActionButton,
    autoMenuActionsCode,
    clickedIdIfMultiIds,
    homeModule,
  } = props;

  const {
    columnsMaxWidths,
    formDatasourceData,
    setFormDatasourceData,
    validationSchemaViewUpdate,
    initialDataUrl,
    finalUpdateRightsRenderer,
    handleUpdateExchangeRate,
  } = useInvoice(actionType, homeModule);

  if (actionType === "create") {
    return (
      <Entity
        initialDataUrl={initialDataUrl}
        readOnly={false}
        actionType="create"
        submitUrl={initialDataUrl}
        onDone={props.onDone}
        selectedData={selectedData}
        idFromRowOption={idFromRowOption}
        formType="multipart"
        readOnlyFields={
          actionType === "create" || actionType === "edit"
            ? [
                {
                  fieldName: "Id",
                  readOnlyRenderer: "integerreadonly",
                },
              ]
            : undefined
        }
        fieldRenderers={rightsRendererCreate}
        validationSchema={validationSchemaCreate}
        readOnlyFieldRenderers={readOnlyRightsRendererCreate}
        nullStringValues={[]}
        fetchConfig={actionType === "create" ? undefined : fetchConfig}
        submitButtonText={submitButtonText}
        hideActionButton={hideActionButton}
        wrapperClassName="Invoices"
        groupColumns={undefined}
        groupSubColumns={
          actionType && actionType.includes("view") && actionType.includes("update")
            ? {
                Details: 3,
                Notes: 3,
              }
            : undefined
        }
        autoMenuActionsCode={getAutoMenuActionsCode(actionType, autoMenuActionsCode)}
        clickedIdIfMultiIds={clickedIdIfMultiIds}
        hideSpecificGroupLabels={
          actionType && actionType.includes("view") && actionType.includes("update-")
            ? ["Details"]
            : undefined
        }
        rightSectionFieldType={
          actionType && actionType.includes("view") && actionType.includes("update-")
            ? "PdfViewerColumn"
            : undefined
        }
        onValuesChange={handleUpdateExchangeRate}
        formDatasourceData={formDatasourceData}
        setFormDatasourceData={setFormDatasourceData}
        disableAutocomplete={true}
      />
    );
  } else {
    const finalValidationSchema = actionType.includes("view")
      ? readOnlyRightsRendererView
      : readOnlyRightsRendererUpdate;

    return (
      <Entity
        initialDataUrl={initialDataUrl}
        readOnly={["view"].includes(actionType)}
        submitUrl={initialDataUrl}
        actionType={actionType.includes("view") ? "view" : "update"}
        onDone={props.onDone}
        selectedData={selectedData}
        idFromRowOption={idFromRowOption}
        formType="multipart"
        hiddenFields={["id"]}
        readOnlyFields={[
          {
            fieldName: "Id",
            readOnlyRenderer: "integerreadonly",
          },
        ]}
        fieldRenderers={finalUpdateRightsRenderer}
        validationSchema={validationSchemaViewUpdate}
        readOnlyFieldRenderers={finalValidationSchema}
        nullStringValues={[]}
        fetchConfig={fetchConfig}
        submitButtonText={submitButtonText}
        hideActionButton={hideActionButton}
        wrapperClassName="Invoices"
        groupColumns={undefined}
        groupsWithBorders={[{ groupName: "Money", groupBorderColour: "#d5d5d5" }]}
        columnsMaxWidths={columnsMaxWidths ?? undefined}
        groupSubColumns={{
          Details: 3,
          Money: 3,
          Notes: 3,
          Approved: 3,
          NCR: 1,
          Compliment: 1,
        }}
        autoMenuActionsCode={getAutoMenuActionsCode(actionType, autoMenuActionsCode)}
        conditionalFields={
          actionType.includes("view")
            ? supplierFeedbackViewConditionalFields
            : supplierFeedbackConditionalFields
        }
        sanitizeConditionalFields={!actionType.includes("view")}
        clickedIdIfMultiIds={clickedIdIfMultiIds}
        hideSpecificGroupLabels={["Details", "Money", "Notes", "Approved", "NCR", "Compliment"]}
        hideViewGroupLabel={true}
        rightSectionFieldType={"PdfViewerColumn"}
        onValuesChange={handleUpdateExchangeRate}
        formDatasourceData={formDatasourceData}
        setFormDatasourceData={setFormDatasourceData}
        disableAutocomplete={true}
      />
    );
  }
}

export default Invoice;
